import draftToHtml from 'draftjs-to-html';
import { FormikHelpers, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { mixed, object } from 'yup';

import Cabecalho from 'Component/Admin/Cabecalho';
import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { Editor } from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import TarefaObservacao from 'Interface/TarefasObservacoes';
import { useAppSelector } from 'Store';
import { useRecuperarTarefaQuery } from 'View/Service/Default/Tarefas';
import {
  useAdicionarTarefaObservacaoMutation,
  useRecuperarTarefasObservacoesQuery,
} from 'View/Service/Default/TarefasObservacoes';

const regras = object({
  texto: mixed().test('obrigatorio', 'Obrigatório', (valor) => {
    if (valor?.blocks?.length && valor.blocks[0].text) {
      return true;
    }

    return false;
  }),
});

const Observacoes = (): React.ReactElement => {
  const { id = 0 } = useParams();
  const usuario = useAppSelector((estado) => estado.autenticacao.usuario);
  const toast = useToast();
  const [adicionarObservacao] = useAdicionarTarefaObservacaoMutation();

  const { data: { tarefa } = {} } = useRecuperarTarefaQuery({
    id: id,
    scope:
      usuario && ['super', 'admin'].includes(usuario.permissao) ? false : true,
  });

  const { data: { observacoes } = {}, refetch } =
    useRecuperarTarefasObservacoesQuery(
      {
        contain: ['Usuarios'],
        limit: 100,
        options: { tarefa_id: tarefa?.id },
        scope:
          usuario && ['super', 'admin'].includes(usuario.permissao)
            ? false
            : true,
      },
      { skip: !tarefa }
    );

  useEffect(() => refetch(), [refetch]);

  const enviar = async (
    observacao: TarefaObservacao,
    form: FormikHelpers<FormikValues>
  ): Promise<void> => {
    try {
      await adicionarObservacao({
        ...observacao,
        tarefa_id: id as number,
      }).unwrap();

      toast({
        tema: 'success',
        conteudo: 'Observação adicionada com sucesso!',
      });

      form.resetForm();
    } catch (erro) {
      toast({
        titulo: 'Erro ao adicionar observação',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <div className="animate__animated animate__fadeIn flex-grow-1 d-flex flex-column">
      <Cabecalho
        titulo={`Chat da tarefa #${tarefa?.id}`}
        descricao={tarefa?.titulo}
      />
      <div className="row justify-content-center align-items-end flex-grow-1">
        <div className="col-sm-10 mt-3 mt-sm-0">
          {observacoes?.length == 0 && (
            <div className="text-center mb-5 pb-5">
              <Icone nome="chat-dots-fill" className="display-1 text-primary" />
              <p className="fs-2 fw-bold mb-0">Nenhuma mensagem enviada</p>
              <p className="lead">Envie uma mensagem para iniciar a conversa</p>
            </div>
          )}
          {observacoes?.map((observacao, chave) => (
            <div
              key={`${observacao.id}-${observacao.modificado}`}
              className="d-flex gap-2 mb-2"
            >
              {observacao.usuario_id != usuario?.id &&
              observacoes[chave - 1]?.usuario_id != observacao.usuario_id ? (
                <Imagem
                  src={observacao.usuario?.imagem}
                  alt={observacao.usuario?.nome}
                  className="rounded-circle object-fit-cover border border-3 border-light shadow"
                  width="50"
                  height="50"
                />
              ) : (
                <>
                  {observacao.usuario_id != usuario?.id && (
                    <div
                      className="flex-shrink-0"
                      style={{ width: '50px' }}
                    ></div>
                  )}
                </>
              )}
              <div
                className={`mb-1 rounded flex-grow-1 p-3 ${
                  observacao.usuario_id != usuario?.id
                    ? 'bg-light border'
                    : 'bg-primary bg-opacity-25'
                }`}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: draftToHtml(observacao.texto),
                  }}
                />
                <small>
                  Por <strong>{observacao.usuario?.nome}</strong> as{' '}
                  <strong>
                    {observacao.criado
                      .data()
                      .toLocaleString(DateTime.DATETIME_SHORT)}
                  </strong>
                </small>
              </div>
              {observacao.usuario_id == usuario?.id &&
              observacoes[chave - 1]?.usuario_id != observacao.usuario_id ? (
                <Imagem
                  src={observacao.usuario?.imagem}
                  alt={observacao.usuario?.nome}
                  className="rounded-circle object-fit-cover border border-3 border-light shadow"
                  width="50"
                  height="50"
                />
              ) : (
                <>
                  {observacao.usuario_id == usuario?.id && (
                    <div
                      className="flex-shrink-0"
                      style={{ width: '50px' }}
                    ></div>
                  )}
                </>
              )}
            </div>
          ))}
          <div className="mt-5 mb-n3 position-relative">
            <Form
              onSubmit={enviar}
              regras={regras}
              desabilitado={tarefa?.status == 1}
            >
              <Editor
                name="texto"
                rotulo="Observações"
                readOnly={tarefa?.status == 1}
              />
              <Botao
                lg
                circulo
                type="submit"
                icone="send"
                className="position-absolute top-0 end-0 translate-middle"
              />
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Observacoes;
